import {
    AdditionalInfo,
    BorderTransportMeans,
    Consignment,
    Container,
    CustomOffice,
    EnsGoodsShipmentItem,
    GoodsShipment,
    IrelandEntrySummaryDeclaration,
    Issuer,
    Packaging,
    TransportEquipment,
} from 'store/declarations/ireland/entry-summary-declaration';
import {
    getCustomerValidation,
    getFloatValidation,
    getExactLengthValidation,
    getMaxValidation,
    getRequiredMessage,
    Shape,
    getNumberOfItemsValidation,
} from 'views/declarations/utils/validation-utils';
import * as Yup from 'yup';

const IDENTITY_CROSSING_BORDER_REGEX = /^(IMO[0-9]{7}|ENI[0-9]{8})$/;
const identityCrossingBorderRegexMessage = 'Expected format is IMO followed by 7 digits or ENI followed by 8 digits.';

const COMBINED_NOMENCLATURE_REGEX = /^[0-9]{4,8}$/;
const combinedNomenclatureRegexMessage = 'Expected format is between 4 and 8 numbers.';

export const transportEquipment = Yup.object()
    .shape<Shape<TransportEquipment>>({
        sealsIdentity: Yup.string().nullable().template(),
    })
    .nullable();

export const customOffice = Yup.object().shape<Shape<CustomOffice>>({
    referenceNumber: getExactLengthValidation('referenceNumber', 8)
        .required(getRequiredMessage('referenceNumber'))
        .nullable()
        .template(),
    expectedDateTimeArrival: Yup.date().required(getRequiredMessage('expectedDateTimeArrival')).nullable().template(),
});

export const subsequentEntryCustomsOffice = () =>
    Yup.object().shape({
        referenceNumber: getExactLengthValidation('referenceNumber', 8)
            .required(getRequiredMessage('referenceNumber'))
            .nullable()
            .template(),
    });

export const sealsIdentity = () =>
    Yup.object().shape({
        sealsIdentity: getMaxValidation('sealsIdentity', 20).required(getRequiredMessage('sealsIdentity')).nullable(),
    });

export const itinerary = () =>
    Yup.object().shape({
        countryOfRoutingCode: Yup.array()
            .of(
                getExactLengthValidation('countryOfRoutingCode', 2)
                    .required(getRequiredMessage('countryOfRoutingCode'))
                    .nullable()
                    .template()
            )
            .template(),
    });

export const borderTransport = () =>
    Yup.object().shape({
        nationalityCrossingBorder: getExactLengthValidation('nationalityCrossingBorder', 2).nullable().template(),
        identityCrossingBorder: Yup.string()
            .required(getRequiredMessage('transportMode'))
            .nullable()
            .test({
                name: 'transport-mode-relation',
                message: identityCrossingBorderRegexMessage,
                test: (value, context) => {
                    const transportMode = (context.options as any)?.from?.at(-1).value?.goodsShipment?.consignment
                        ?.borderTransportMeans?.transportMode;

                    if (transportMode === 1 || transportMode === 8) {
                        if (!value?.match(IDENTITY_CROSSING_BORDER_REGEX)) {
                            return false;
                        }
                    }

                    return true;
                },
            })
            .template(),
    });

const borderTransportMeans = Yup.object().shape<Shape<BorderTransportMeans>>({
    identityCrossingBorder: Yup.string()
        .nullable()
        .when('transportMode', {
            is: (val: string) => val === '1' || val === '8',
            then: getMaxValidation('identityCrossingBorder', 11)
                .matches(IDENTITY_CROSSING_BORDER_REGEX, identityCrossingBorderRegexMessage)
                .nullable(),
        })
        .template(),
    nationalityCrossingBorder: getExactLengthValidation('nationalityCrossingBorder', 2).nullable().template(),
    conveyanceReferenceNumber: getMaxValidation('conveyanceReferenceNumber', 35).nullable().template(),
    transportMode: getMaxValidation('conveyanceReferenceNumber', 2)
        .required(getRequiredMessage('transportMode'))
        .nullable()
        .template(),
    itinerary: itinerary(),
});

export const productPackages = Yup.object().shape<Shape<Packaging>>({
    marks: getMaxValidation('marks', 140).nullable().template(),
    type: getExactLengthValidation('kindOfPackages', 2)
        .required(getRequiredMessage('kindOfPackages'))
        .nullable()
        .template(),
    numberOfPackages: getMaxValidation('numberOfPackages', 5).nullable().template(),
    numberOfPieces: getMaxValidation('numberOfPieces', 5).nullable().template(),
});

export const issuer = Yup.object().shape<Shape<Issuer>>({
    place: Yup.string().max(35).required(getRequiredMessage('declarationIssuingPlace')).nullable().template(),
});

export const additionalDocumentSchema = Yup.object().shape<Shape<AdditionalInfo>>({
    code: getMaxValidation('additionalDocumentIdentifier', 4)
        .required(getRequiredMessage('additionalDocumentIdentifier'))
        .nullable()
        .template(),
    identifier: getMaxValidation('additionalDocumentText', 255)
        .required(getRequiredMessage('additionalDocumentText'))
        .nullable()
        .template(),
});

export const additionalInformation = Yup.object().shape<Shape<AdditionalInfo>>({
    code: getMaxValidation('additionalInformationText', 17)
        .required(getRequiredMessage('additionalInformationIdentifier'))
        .nullable()
        .template(),
    identifier: getMaxValidation('additionalInformationText', 255).nullable().template(),
});

export const containers = Yup.object().shape<Shape<Container>>({
    equipmentIdentification: getMaxValidation('containerNumber', 11)
        .required(getRequiredMessage('containerNumber'))
        .nullable()
        .template(),
});

export const goodsItems = Yup.object().shape<Shape<EnsGoodsShipmentItem>>({
    ucr: getMaxValidation('ucr', 35).nullable().template(),
    transportChargesMethodOfPayment: getExactLengthValidation('transportChargesMethodOfPayment', 1)
        .nullable()
        .template(),
    loadingPlace: getMaxValidation('loadingPlace', 35).nullable().template(),
    unloadingPlace: getMaxValidation('unloadingPlace', 35).nullable().template(),
    grossMass: getFloatValidation('grossMass', 8, 3).nullable().template(),
    commodity: Yup.object().shape({
        description: getMaxValidation('description', 280).nullable().template(),
        combinedNomenclature: Yup.string()
            .matches(COMBINED_NOMENCLATURE_REGEX, combinedNomenclatureRegexMessage)
            .required(getRequiredMessage('combinedNomenclature'))
            .nullable()
            .template(),
        dangerousCode: getExactLengthValidation('dangerousCode', 4).nullable().template(),
    }),
    borderTransportMeans: Yup.array().of(borderTransport()).nullable(),
    consignor: getCustomerValidation({ name: { max: 35 } }).nullable(),
    consignee: getCustomerValidation({ name: { max: 35 } }).nullable(),
    notifyParty: getCustomerValidation({ name: { max: 35 } }).nullable(),
});

export const consignment = Yup.object().shape<Shape<Consignment>>({
    borderTransportMeans: borderTransportMeans,
    inlandModeOfTransport: Yup.string().nullable(),
    locationOfGoods: Yup.string().nullable(),
    loadingPlace: getMaxValidation('loadingPlace', 35).nullable().template(),
    unloadingPlace: getMaxValidation('unloadingPlace', 35).nullable().template(),
    transportEquipment,
});

const goodsShipment = Yup.object().shape<Shape<GoodsShipment>>({
    ucr: getMaxValidation('ucr', 35).nullable().template(),
    firstEntryCustomsOffice: customOffice.required('Custom office of first entry is required'),
    consignment: consignment,
    consignor: getCustomerValidation({ name: { max: 35 } }).nullable(),
    consignee: getCustomerValidation({ name: { max: 35 } }).nullable(),
    notifyParty: getCustomerValidation({ name: { max: 35 } }).nullable(),
    transportMethodOfPaymentCode: getExactLengthValidation('transportChargesMethodOfPayment', 1).nullable().template(),
    goodsItems: Yup.array().of(goodsItems).required('The declaration needs to contain at least one item.'),
});

const irelandEnsValidation = (numberOfItems?: number) =>
    Yup.object().shape<Shape<IrelandEntrySummaryDeclaration>>({
        issuing: issuer,
        numberOfItems: getNumberOfItemsValidation(numberOfItems)
            .required(getRequiredMessage('numberOfItems'))
            .nullable()
            .template(),
        totalNumberOfPackages: getMaxValidation('totalNumberOfPackages', 7).nullable().template(),
        totalGrossMass: getMaxValidation('totalGrossMass', 11).nullable().template(),
        specificCircumstanceIndicator: getExactLengthValidation('specificCircumstanceIndicator', 1)
            .nullable()
            .template(),
        agent: getCustomerValidation({ eori: { required: true }, name: { max: 35 } }).required(
            getRequiredMessage('agent')
        ),
        carrier: getCustomerValidation({ name: { max: 35 } }).nullable(),
        // Representative
        personLodgingTheSummaryDeclaration: getCustomerValidation({ name: { max: 35 } }).nullable(),
        goodsShipment: goodsShipment,
    });

export default irelandEnsValidation;
